import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShopifyProduct, formatPrice, calculateDiscountedPrice, DISCOUNT_PERCENTAGE } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { Button } from "@/components/ui/button";
import { ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { WishlistButton } from "@/components/shop/WishlistButton";

interface ProductCardProps {
  product: ShopifyProduct;
  index?: number;
}

export const ProductCard = ({ product, index = 0 }: ProductCardProps) => {
  const { node } = product;
  const addItem = useCartStore(state => state.addItem);
  
  const firstVariant = node.variants.edges[0]?.node;
  const firstImage = node.images.edges[0]?.node;
  const price = node.priceRange.minVariantPrice;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!firstVariant) return;
    
    addItem({
      product,
      variantId: firstVariant.id,
      variantTitle: firstVariant.title,
      price: firstVariant.price,
      quantity: 1,
      selectedOptions: firstVariant.selectedOptions,
    });
    
    toast.success("Added to cart", {
      description: node.title,
      position: "top-center",
    });
  };

  const originalPrice = price.amount;
  const discountedPrice = calculateDiscountedPrice(originalPrice);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="group"
    >
      <Link to={`/shop/${node.handle}`} className="block">
        <div className="aspect-square bg-secondary/30 overflow-hidden mb-3 md:mb-4 relative group/image rounded-lg">
          {/* Discount Badge */}
          <div className="absolute top-2 md:top-3 left-2 md:left-3 z-10">
            <Badge className="bg-destructive text-destructive-foreground font-semibold text-xs px-1.5 py-0.5">
              -{DISCOUNT_PERCENTAGE}%
            </Badge>
          </div>
          
          {/* Wishlist Button */}
          <div className="absolute top-2 md:top-3 right-2 md:right-3 z-10 opacity-0 group-hover/image:opacity-100 transition-opacity">
            <WishlistButton product={product} size="sm" />
          </div>
          
          {firstImage ? (
            <img
              src={firstImage.url}
              alt={firstImage.altText || node.title}
              loading="lazy"
              decoding="async"
              width={400}
              height={400}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <ShoppingBag className="h-10 md:h-12 w-10 md:w-12" />
            </div>
          )}
        </div>
        
        <div className="space-y-1.5 md:space-y-2">
          <h3 className="font-display text-base md:text-lg font-light group-hover:text-primary transition-colors line-clamp-2">
            {node.title}
          </h3>
          
          {/* Description hidden on product cards - shown on product detail page */}
          
          <div className="flex items-center justify-between pt-1 md:pt-2">
            <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
              <span className="text-xs md:text-sm text-muted-foreground line-through">
                {formatPrice(originalPrice, price.currencyCode)}
              </span>
              <span className="font-semibold text-destructive text-sm md:text-base">
                {formatPrice(discountedPrice, price.currencyCode)}
              </span>
            </div>
            
            <Button
              size="sm"
              variant="outline"
              onClick={handleAddToCart}
              className="opacity-0 group-hover:opacity-100 transition-opacity hidden sm:flex text-xs h-8"
            >
              <ShoppingBag className="h-3.5 w-3.5 mr-1.5" />
              Add
            </Button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
